// Configuration for Customer Suggestions component

// Environment configuration
export const config = {
  // API configuration
  api: {
    baseUrl: process.env.REACT_APP_API_BASE_URL || '/admin-panel/suggestions',
    timeout: 10000, // 10 seconds
    retryAttempts: 3,
    retryDelay: 1000 // 1 second
  },
  
  // Pagination configuration
  pagination: {
    defaultPageSize: 10,
    pageSizeOptions: [5, 10, 20, 50, 100],
    maxPageSize: 100
  },
  
  // Filter configuration
  filters: {
    statusOptions: [
      { value: '', label: 'All Status' },
      { value: 'PENDING', label: 'Pending' },
      { value: 'UNDER_REVIEW', label: 'Under Review' },
      { value: 'APPROVED', label: 'Approved' },
      { value: 'REJECTED', label: 'Rejected' },
      { value: 'IMPLEMENTED', label: 'Implemented' }
    ],
    
    typeOptions: [
      { value: '', label: 'All Types' },
      { value: 'FEATURE', label: 'Feature Request' },
      { value: 'SHOP_ONBOARDING', label: 'Shop Onboarding' },
      { value: 'IMPROVEMENT', label: 'Improvement' },
      { value: 'OTHER', label: 'Other' }
    ],
    
    priorityOptions: [
      { value: '', label: 'All Priorities' },
      { value: 'LOW', label: 'Low' },
      { value: 'MEDIUM', label: 'Medium' },
      { value: 'HIGH', label: 'High' },
      { value: 'URGENT', label: 'Urgent' }
    ]
  },
  
  // Sorting configuration
  sorting: {
    defaultSortBy: 'createdAt',
    defaultSortOrder: 'desc',
    sortOptions: [
      { value: 'createdAt', label: 'Created Date' },
      { value: 'updatedAt', label: 'Updated Date' },
      { value: 'title', label: 'Title' },
      { value: 'status', label: 'Status' },
      { value: 'priority', label: 'Priority' },
      { value: 'votes.upvotes', label: 'Upvotes' }
    ]
  },
  
  // Export configuration
  export: {
    formats: ['json', 'csv'],
    maxRecords: 10000,
    filename: {
      json: 'customer-suggestions.json',
      csv: 'customer-suggestions.csv'
    }
  },
  
  // UI configuration
  ui: {
    debounceDelay: 300, // milliseconds for search input debouncing
    animationDuration: 200, // milliseconds for transitions
    toastDuration: 5000, // milliseconds for notification display
    
    // Color themes
    colors: {
      primary: '#3b82f6',
      secondary: '#64748b',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      info: '#06b6d4'
    },
    
    // Status colors
    statusColors: {
      PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
      UNDER_REVIEW: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
      APPROVED: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200' },
      REJECTED: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
      IMPLEMENTED: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' }
    },
    
    // Priority colors
    priorityColors: {
      LOW: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200' },
      MEDIUM: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
      HIGH: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
      URGENT: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' }
    }
  },
  
  // Feature flags
  features: {
    enableExport: true,
    enableBulkActions: false,
    enableComments: false,
    enableNotifications: true,
    enableAnalytics: true,
    enableRealTimeUpdates: false
  },
  
  // Validation rules
  validation: {
    title: {
      minLength: 5,
      maxLength: 200,
      required: true
    },
    description: {
      minLength: 10,
      maxLength: 1000,
      required: true
    },
    adminNotes: {
      maxLength: 500,
      required: false
    }
  },
  
  // Performance settings
  performance: {
    enableVirtualization: false, // Enable for large datasets
    lazyLoadImages: true,
    cacheTimeout: 300000, // 5 minutes
    prefetchNextPage: true
  }
};

// Helper function to get configuration value with fallback
export const getConfig = (path, fallback = null) => {
  const keys = path.split('.');
  let value = config;
  
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return fallback;
    }
  }
  
  return value;
};

// Helper function to check if feature is enabled
export const isFeatureEnabled = (featureName) => {
  return getConfig(`features.${featureName}`, false);
};

// Helper function to get API configuration
export const getApiConfig = () => {
  return getConfig('api', {});
};

// Helper function to get UI colors
export const getStatusColor = (status) => {
  return getConfig(`ui.statusColors.${status}`, getConfig('ui.statusColors.PENDING'));
};

export const getPriorityColor = (priority) => {
  return getConfig(`ui.priorityColors.${priority}`, getConfig('ui.priorityColors.LOW'));
};

export default config;